// An area's home, like the department's own site: the area's header with Courage Chat, its analytics, its apps as
// tiles with what's open in each, its Business Central pages, then your records and the latest activity, and the
// area's processes and tables.
import { useMemo } from "react";
import { STATE_LABEL } from "../factory/factoryApi";
import { Link, useParams } from "react-router-dom";
import { api } from "../api";
import { useLoad } from "../hooks";
import { BcSection, appsOf, functionsOf } from "./Home";
import { ago, label, recordHref } from "../ui";
import { Chat } from "../Chat";
import { findArea } from "../suite";
import { AreaTables } from "../productivity/Home";
import { AreaProcesses } from "../processes/ProcessLibrary";
import { AppTiles, AreaAnalytics, engineApps, SiteHeader, specFor } from "../areaSite";

const TYPE_NAME: Record<string, string> = { account: "Account", opportunity: "Opportunity", quote: "Quote", issue: "Issue", capa: "CAPA", eco: "Change Order", contract: "Contract", lead: "Lead", campaign: "Campaign", content: "Content", ticket: "Ticket", work_order: "Work Order", asset: "Asset", pm: "PM", factory_request: "Project", item_setup: "Item Setup" };
const usd = (n: number | null) => (n === null ? "" : `${n.toLocaleString("en-US", { maximumFractionDigits: 0 })} USD`);
const usDate = (d: string | null) => (d ? new Date(d.slice(0, 10) + "T00:00:00").toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) : "");
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** What a record is called: its record type, or for an app on the shared record engine, its noun. */
function typeName(type: string) {
  if (type.startsWith("app:")) { const s = specFor(type.slice(4)); return s ? cap(s.noun) : label(type.slice(4)); }
  return TYPE_NAME[type] ?? label(type);
}
/** A stage's name as the app calls it ("MRB review"), else the state in words. */
function stateName(type: string, state: string | null) {
  if (!state) return "";
  if (type.startsWith("app:")) { const st = specFor(type.slice(4))?.stages.find((x) => x.id === state); if (st) return st.name; }
  if (type === "factory_request") return STATE_LABEL[state] ?? label(state);
  return label(state);
}

export function AreaPage() {
  const { id = "" } = useParams();
  const d = findArea(id);
  const engine = useMemo(() => (d ? engineApps(d) : { ids: [], done: [] }), [d]);
  const { data: home, reload } = useLoad(() => (d ? api.home(d.id, engine.ids, engine.done) : Promise.resolve(null)), [id]);
  if (!d) return <div className="page"><div className="panel empty">That area doesn't exist. <Link to="/">Go home</Link></div></div>;
  const fns = functionsOf(d);
  const hasApps = appsOf(d).length > 0;

  return (
    <div className="page area-home area-site">
      <SiteHeader d={d}>
        <Chat area={d.id} compact placeholder={`Ask about ${d.name}, or ask for an update`} onChanged={() => reload()} />
      </SiteHeader>

      <AreaAnalytics d={d} />

      {hasApps && (
        <section aria-labelledby="apps-h">
          <div className="sec-h"><h2 id="apps-h">Apps</h2></div>
          <AppTiles d={d} home={home ?? null} />
        </section>
      )}
      <section aria-label="In Business Central"><BcSection id={d.id} fns={fns} defaultOpen /></section>
      {!fns.length && <p className="muted">No apps here yet. Add your company's links in Admin, Company links.</p>}

      <div className="area-cols">
        <section className="panel" aria-labelledby="mine-h">
          <div className="panel-h"><h2 id="mine-h" className="ph">My records</h2><span className="muted">Open, that you own or created</span></div>
          {!home ? <div className="empty">Loading…</div> : home.records.length ? (
            <ul className="rec-list">
              {home.records.map((r) => (
                <li key={r.id}>
                  <Link to={recordHref(r.type, r.number)} className="rl-main">
                    <span className="rl-t">{r.title}</span>
                    <span className="rl-m">{r.number} · {typeName(r.type)}{r.accountName ? ` · ${r.accountName}` : ""}</span>
                  </Link>
                  <span className="rl-side">
                    <span className="rl-state">{stateName(r.type, r.state)}</span>
                    <span className="rl-m">{[usd(r.amount), r.nextDue ? `due ${usDate(r.nextDue)}` : r.closeDate ? `closes ${usDate(r.closeDate)}` : ""].filter(Boolean).join(", ")}</span>
                  </span>
                </li>
              ))}
            </ul>
          ) : <div className="empty">Nothing open that you own here yet.</div>}
        </section>

        <section className="panel" aria-labelledby="act-h">
          <div className="panel-h"><h2 id="act-h" className="ph">Latest activity</h2></div>
          {!home ? <div className="empty">Loading…</div> : home.activity.length ? (
            <ol className="act-list">
              {home.activity.map((a, i) => (
                <li key={i}>
                  <span className="al-w">{a.from && a.to ? `${stateName(a.type, a.from)} to ${stateName(a.type, a.to)}` : a.what}</span>
                  <span className="al-m"><Link to={recordHref(a.type, a.number)}>{a.number}</Link> {a.title}</span>
                  <span className="al-m">{a.by}, {ago(a.at)}</span>
                </li>
              ))}
            </ol>
          ) : <div className="empty">No activity yet.</div>}
        </section>
      </div>

      <AreaProcesses area={d.id} name={d.name} />
      <AreaTables area={d.id} name={d.name} />
    </div>
  );
}
